import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { DerivedValue } from "@/features/admin/components";

export interface SlugFieldProps {
  /**
   * `true` al editar. Es lo ÚNICO que decide si este bloque se pinta: al
   * crear no hay nada que enseñar todavía.
   */
  locked: boolean;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * La dirección pública del producto. Ya NO es un campo.
 *
 * ── Por qué dejó de serlo ───────────────────────────────────────────
 * "En ningún momento te pedí este campo" (Anahí, 26-ago), señalando el
 * slug en el paso de clasificación. Y es cierto que sobra: la dirección
 * de la ficha no es una decisión que quien da de alta un producto tenga
 * que tomar. Se genera del nombre en español —lo hace
 * `productFormSchema` al enviar, con `slugify` del contrato— y ya está.
 *
 * ── Qué pasa con los choques, ahora que no hay dónde corregirlos ────
 * Dos productos que se llamen igual generan el mismo slug. Antes eso era
 * un 409 con un "elige otro", y había campo donde elegirlo; ahora sería
 * un "Crear producto" que no hace nada. Lo resuelve el SERVIDOR poniendo
 * el primer sufijo libre (`colageno-2`), que es lo que hace cualquier
 * gestor de contenidos y por la misma razón.
 *
 * ── Y por qué al EDITAR sí se enseña ────────────────────────────────
 * Porque es la dirección de una ficha que ya existe, y hay que poder
 * copiarla, comprobarla y entender por qué un enlace apunta donde apunta.
 * De SOLO LECTURA, con `DerivedValue`: un slug no se puede cambiar sin
 * romper cada enlace ya compartido —el mensaje de WhatsApp, el correo, el
 * resultado de Google, el QR impreso—, así que el contrato lo omite de
 * `productUpdateSchema` y aquí no hay `<input>` que invite a intentarlo.
 *
 * `DerivedValue` es exactamente el componente para esto: existe para los
 * valores que NADIE tecleó, y se marca de tres formas a la vez para que
 * no se confunda con un campo.
 * ═══════════════════════════════════════════════════════════════════
 */
export function SlugField({ locked }: SlugFieldProps) {
  const { t } = useTranslation();
  const slug = (useWatch({ name: "slug" }) ?? "") as string;

  if (!locked || slug.trim() === "") return null;

  return (
    <DerivedValue
      label={t("Dirección en la tienda")}
      value={<span className="font-mono text-[13px]">/{slug}</span>}
      note={t("No se cambia: rompería los enlaces ya compartidos.")}
    />
  );
}
